import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useDraft } from "../lib/drafts";
import type { PanelEntry, PanelInteraction } from "../lib/api";
import { panelHref } from "../lib/workspaces";
import { conversationHref } from "../connections/navigation";
import { Icon } from "../components/Icon";
import { pendingInteractionPanels } from "../shell/ownerShell";
import { PanelView } from "./PanelView";
import { ConversationViewport } from "../lib/conversationViewport";
import "./InteractionFrame.css";

export const FormDraftScope = createContext("");

/**
 * How many questions the request in the dialog puts. The form is drawn inside the dialog, closed or open, and
 * is the one that knows; the bar above the composer says it before the dialog is opened.
 */
export const AskReport = createContext<((questions: number | null) => void) | null>(null);

/**
 * One request the Agent is waiting on.  A card or a dialog is one quiet bar above the composer and its
 * form opens only when the Owner presses 回答: not by itself, not again after a reload, and not as a
 * block that squeezes the thread.  The bar goes when the request is no longer pending, so an
 * answer given here, in the conversation or by the Agent withdrawing it all end the same way.
 * Closing the form never resolves the request.
 */
export function InteractionFrame({ request, title, children }: { request: PanelInteraction; title: string; children: ReactNode }) {
  if (request.placement === "sidebar") return <InteractionSidebar request={request} title={title}>{children}</InteractionSidebar>;
  return <InteractionRow title={title}>{children}</InteractionRow>;
}

function InteractionRow({ title, children }: { title: string; children: ReactNode }) {
  // Never remembered: what is kept is the Owner's typing (the form's own draft), not that the form was open.
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState<number | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    else if (!open && node.open) node.close();
  }, [open]);
  return <AskReport.Provider value={setQuestions}>
    <section className="ask-bar request-row pa-open-group" aria-label={title}>
      <span className="ask-bar-icon" aria-hidden="true"><Icon name="question" size={18} /></span>
      <div className="ask-bar-words">
        <span className="ask-bar-kicker">在等你回答{questions ? ` · ${questions} 个问题` : ""}</span>
        <span className="ask-bar-title" data-quoted="true">{title}</span>
      </div>
      <button className="btn btn-accent ask-bar-answer" type="button" aria-haspopup="dialog" aria-label={`${title}，回答`} onClick={() => setOpen(true)}>回答</button>
    </section>
    <dialog ref={dialog} className="interaction-dialog pa-dialog" aria-label={title}
      onCancel={event => { event.preventDefault(); setOpen(false); }} onClose={() => setOpen(false)}>
      <button type="button" className="btn btn-quiet btn-icon interaction-dialog-close" aria-label="收起，稍后回应" title="收起，稍后回应"
        onClick={() => setOpen(false)}><Icon name="no" size={18} /></button>
      {children}
    </dialog>
  </AskReport.Provider>;
}

function InteractionSidebar({ request, title, children }: { request: PanelInteraction; title: string; children: ReactNode }) {
  const [collapsed, setCollapsed] = useDraft(`interaction:${request.request_id}:collapsed`, false);
  return <aside className="interaction-sidebar pa-bounded-surface" aria-label={title}>
    <button type="button" className="btn btn-quiet" onClick={() => setCollapsed(!collapsed)}>
      {collapsed ? `继续回应：${title}` : "收起，稍后回应"}
    </button>
    <div hidden={collapsed}>{children}</div>
  </aside>;
}

/** A conversation identifies where a request was asked, not which work it belongs to. */
export function InteractionShelf({ panels, sessionId, currentPanel, onToast, seatKnown, presentation = "workspace" }: {
  panels: PanelEntry[]; sessionId: string; currentPanel?: string;
  onToast: (message: string, tone: "good" | "critical") => void; seatKnown: boolean;
  /** A conversation can answer a request without presenting it as the open work's requirement. */
  presentation?: "workspace" | "conversation";
}) {
  const pending = panels.filter(panel => panel.key !== currentPanel && panel.interaction
    && (panel.interaction.status ?? "pending") === "pending");
  if (presentation === "conversation") return <ConversationRequests panels={panels} pending={pendingInteractionPanels(panels)}
    sessionId={sessionId} onToast={onToast} seatKnown={seatKnown} />;
  // Requests currently declare their original conversation, but no task ownership. On a work page,
  // even a request from the same conversation cannot be presented as that work's requirement.
  const besideConversation = (panel: PanelEntry) => !currentPanel && panel.interaction!.session_id === sessionId
    && panel.interaction!.placement !== "page";
  const unassigned = pending.filter(panel => !besideConversation(panel));
  return <>{pending.filter(besideConversation).map(panel =>
    <InteractionFrame key={`${panel.key}:${panel.interaction?.version}`} request={panel.interaction!} title={panel.title}>
      <PanelView panelKey={panel.key} panels={panels} onToast={onToast} seatKnown={seatKnown} heading="h2" />
    </InteractionFrame>)}
    {unassigned.length > 0 && <details className="interaction-unassigned pa-open-group">
      <summary>Agent 待答 · 未注明任务（{unassigned.length}）</summary>
      <p>这些请求未注明所属任务，不能确定属于当前工作。可以查看原对话，或单独回应。</p>
      <ul>{unassigned.map(panel => {
        const request = panel.interaction!;
        return <li key={`${panel.key}:${request.version}`}>
          <div className="interaction-unassigned-copy">
            <span data-quoted="true">{panel.title}</span>
            <small>{request.session_id === sessionId ? "来自这段对话" : "来自另一段对话"}</small>
          </div>
          <div className="interaction-unassigned-actions">
            <a className="btn btn-quiet" href={conversationHref(request.session_id, request.owner_conversation)}>
              {request.owner_conversation === false ? "查看工作区" : "查看原对话"}
            </a>
            <a className="btn" aria-label={`${panel.title}，回应`}
              href={panelHref(panel.key, { session: request.session_id, session_id: request.session_id })}>回应</a>
          </div>
        </li>;
      })}</ul>
    </details>}
  </>;
}

function linkedRequest(): string {
  const [path, query] = window.location.hash.split("?");
  return path === "#/chat" ? new URLSearchParams(query).get("request") ?? "" : "";
}

/** The form remains in this chat and submits through its original request, never through the ordinary composer. */
function ConversationRequests({ panels, pending, sessionId, onToast, seatKnown }: {
  panels: PanelEntry[]; pending: PanelEntry[]; sessionId: string;
  onToast: (message: string, tone: "good" | "critical") => void; seatKnown: boolean;
}) {
  const [linked, setLinked] = useState(linkedRequest);
  const [opened, setOpened] = useState(linkedRequest);
  useEffect(() => {
    const changed = () => {
      const request = linkedRequest();
      setLinked(request);
      // Moving between the work page and enlarged chat keeps this same form open.
      if (request) setOpened(request);
    };
    window.addEventListener("hashchange", changed);
    return () => window.removeEventListener("hashchange", changed);
  }, []);
  if (!pending.length) return null;
  return <section className="chat-requests" aria-label="需要你回应" tabIndex={-1}>
    <h2 className="chat-requests-heading"><Icon name="question" size={16} />需要你回应<span>{pending.length}</span></h2>
    {pending.map(panel => <ConversationRequest key={`${panel.key}:${panel.interaction!.version}`} panel={panel} panels={panels}
      sessionId={sessionId} open={opened === panel.interaction!.request_id} linked={linked === panel.interaction!.request_id}
      onToggle={() => setOpened(value => value === panel.interaction!.request_id ? "" : panel.interaction!.request_id)}
      onToast={onToast} seatKnown={seatKnown} />)}
  </section>;
}

function ConversationRequest({ panel, panels, sessionId, open, linked, onToggle, onToast, seatKnown }: {
  panel: PanelEntry; panels: PanelEntry[]; sessionId: string; open: boolean; linked: boolean;
  onToggle: () => void; onToast: (message: string, tone: "good" | "critical") => void; seatKnown: boolean;
}) {
  const request = panel.interaction!;
  const contentId = useId();
  const row = useRef<HTMLElement>(null);
  const hold = useContext(ConversationViewport);
  useLayoutEffect(() => {
    if (!open || !row.current) return;
    const release = hold?.(row.current);
    if (linked) {
      if (!hold) row.current.scrollIntoView?.({ block: "start" });
      row.current.focus({ preventScroll: true });
    }
    return release;
  }, [linked, open, hold]);
  return <article ref={row} className="chat-request pa-bounded-surface" tabIndex={-1} data-request-id={request.request_id} data-open={open || undefined}>
    <div className="chat-request-heading">
      <div><h3 data-quoted="true">{panel.title}</h3>{request.owner_conversation === false || request.session_id !== sessionId
        ? <p>{request.owner_conversation === false ? "来自这个 Agent 的后台工作" : "来自这个 Agent 的另一段对话"}</p> : null}</div>
      <button className={`btn ${open ? "btn-quiet" : "btn-accent"}`} type="button" onClick={onToggle}
        aria-label={`${panel.title}，${open ? "收起" : "回答"}`} aria-expanded={open} aria-controls={contentId}>
        {open ? "收起" : "回答"}<Icon name={open ? "up" : "down"} size={14} />
      </button>
    </div>
    {open ? <div id={contentId} className="chat-request-form">
      <PanelView panelKey={panel.key} panels={panels} onToast={onToast} seatKnown={seatKnown} heading="h2"
        context={{ session_id: request.session_id }} presentation="embedded" />
    </div> : null}
  </article>;
}
